/**
 * The live match connection, and the view state it produces.
 *
 * This is the only file that knows the wire protocol. Everything above it reads
 * a plain `MatchView` and calls `sendMove`.
 *
 * Three things here are easy to get wrong and expensive to debug:
 *
 * 1. **The personal view is an overlay, not a replacement.** `personal.view`
 *    carries only what this seat sees beyond the public view, per zone. Merging
 *    by replacing the zones map blanks every other player's card count.
 *
 * 2. **An omitted section means unchanged.** A packet with only `table` must
 *    leave the personal state exactly as it was, and the other way round.
 *
 * 3. **A move carries the PERSONAL version**, and host actions carry the table
 *    version. Sending the wrong one is an immediate STALE.
 */

import { ClientOpCode, ServerOpCode } from '@cards/protocol';
import type { GameView } from './merge.js';
import type { Session, Socket } from '@heroiclabs/nakama-js';
import { client, useSSL } from './client.js';
import { mergeView } from './merge.js';

export type { GameView, ZoneView } from './merge.js';

export interface SeatInfo {
  seat: number;
  displayName: string;
  avatarId: string;
  connected: boolean;
  away: boolean;
  isHost: boolean;
}

export interface TurnInfo {
  seat: number;
  /**
   * True only when every active seat is doing the same thing.
   *
   * Hearts' pass is shared: everybody stages at once and nobody is "on turn".
   * Cheat's challenge window is not: one player is claiming and the rest are
   * deciding whether to believe them, so there is a turn holder even though
   * several seats can act.
   */
  shared?: boolean;
  remainingMs: number;
  limitMs: number;
  /** Remaining time at which to start warning. Config, sent with the turn. */
  warningMs?: number;
}

export interface ObserverInfo {
  /** Match-local and arrival-ordered. Not the Nakama user id, deliberately. */
  id: number;
  displayName: string;
  avatarId: string;
}

/** A range of legal amounts, for a move whose answers cannot be listed. */
export interface MoveRange {
  min: number;
  max: number;
  step: number;
  /** Amounts worth reaching in one tap, named by the game. */
  presets?: { label: string; value: number }[];
}

/** Somebody in the voice conversation. `seat` is null for an observer. */
export interface VoiceMember {
  seat: number | null;
  micOff: boolean;
  earsOff: boolean;
}

/** One thing said at the table, as the server recorded it. */
export interface ChatEntry {
  seat: number;
  /** Exactly one of these two. A phrase is carried by id, never by its words. */
  phraseId?: string;
  text?: string;
  /** Monotonic across the match, so a new message is never mistaken for a redraw. */
  n: number;
}

export interface LegalMove {
  move: string;
  args: string[];
  /** Present only for a move whose argument is a number in an interval. */
  range?: MoveRange;
}

export type Lifecycle = 'lobby' | 'playing' | 'between';

/**
 * A table only one person can sit at.
 *
 * `seatRange.max` is the GAME's maximum, so this is true of a patience from
 * the moment the room opens and never true of a table that merely happens to
 * have one person at it. A good deal of the room is built for people who are
 * not coming - the invite, the clock, the chat, the watchers - and this is the
 * one question every part of it asks before offering them.
 */
export function isSolo(view: { seatRange: { max: number } }): boolean {
  return view.seatRange.max === 1;
}

export interface MatchView {
  /** Null until the first table packet arrives. */
  tableV: number;
  personalV: number;
  /** This client's seat, or null while spectating. */
  seat: number | null;
  seats: SeatInfo[];
  /** Everybody in the room who is not playing. Listed by name, on purpose. */
  observers: ObserverInfo[];
  activeSeats: number[];
  phase: string | null;
  turn: TurnInfo | null;
  paused: boolean;
  /** The move just applied, for the table to animate. A counter, not a flag. */
  lastMove: { n: number; seat: number; move: string } | null;
  /**
   * The room is not coming back.
   *
   * Set when reconnecting has been given up on - a match that no longer
   * exists, or a network that never returned. Distinct from `connected:
   * false`, which means "trying".
   */
  gone: boolean;
  /** Spectators who came to play and are waiting for a chair. */
  waiting: number;
  /** What each seat last said, by phrase id, with a count. Keyed by seat. */
  /** The bubble over each seat: a phrase id or typed words, with a count. */
  says: Record<string, { id?: string; text?: string; n: number }>;
  /**
   * Everything said this match, oldest first.
   *
   * Arrives in its own section with its own version, so it is only re-sent when
   * somebody speaks — not on every card played. See `chatV`.
   */
  chatLog: ChatEntry[];
  chatV: number;
  /** Whether the host allows typing. The phrase list is always available. */
  allowTextChat: boolean;
  /**
   * Whether the host allows voice at this table (L3).
   *
   * Separate from whether the deployment can carry voice at all, which comes
   * from `client_config` and is not something a host controls.
   */
  allowVoice: boolean;
  /**
   * Who is in the voice conversation, keyed by user id.
   *
   * By user rather than by seat, because an observer can be in it and has no
   * chair — and because a user id is what a signalling blob is addressed to.
   */
  voice: Record<string, VoiceMember>;
  /** The game this room is playing now. Changes when the host picks another. */
  gameId: string;
  /** Where the ROOM is, as opposed to where the game is. */
  lifecycle: Lifecycle;
  /** The room is in its lobby because the host stopped the game, not because
      the game finished. Cleared by the next deal. */
  endedByHost: boolean;
  /**
   * What this table is painted in, and who supplied it (D87).
   *
   * The host's equipped deck and table, sent to everybody. Null before the
   * first packet and for a room with no acting host, both of which mean the
   * free theme.
   */
  skin: { deckId: string; tableId: string; avatarIds?: string[]; by?: string } | null;
  round: number;
  autoContinue: boolean;
  seatCount: number;
  /** May anybody join this room without taking a seat? */
  allowObservers: boolean;
  /** What the game needs, and the most this room may seat. */
  seatRange: { min: number; max: number };
  /** How many are sitting down right now. Seats follow players. */
  seated: number;
  /** The room setting, which survives a lobby and the gaps between rounds. */
  turnLimitMs: number;
  /** What this table CHOSE, by variant id. The declaration comes with the join. */
  variants: Record<string, boolean | string | number>;
  /** The finished round's result, while between rounds. */
  lastResult: unknown;
  /** True when this client holds the host seat. */
  isHost: boolean;
  /** Whoever holds host powers is standing in for the host who opened the room. */
  hostIsTemporary: boolean;
  /** The room is finishing. Players go back to the main page. */
  closing: boolean;
  /** Public view merged with this seat's private overlay. */
  view: GameView;
  legalMoves: LegalMove[];
  gameOver: unknown | null;
  connected: boolean;
  lastError: { code: string; message: string } | null;
}

export function emptyView(): MatchView {
  return {
    tableV: -1,
    personalV: -1,
    seat: null,
    seats: [],
    observers: [],
    activeSeats: [],
    phase: null,
    turn: null,
    paused: false,
    lastMove: null,
    gone: false,
    waiting: 0,
    says: {},
    chatLog: [],
    chatV: -1,
    allowTextChat: true,
    allowVoice: true,
    voice: {},
    gameId: '',
    lifecycle: 'lobby',
    endedByHost: false,
    skin: null,
    round: 0,
    autoContinue: true,
    seatCount: 0,
    allowObservers: true,
    seatRange: { min: 0, max: 0 },
    seated: 0,
    turnLimitMs: 0,
    variants: {},
    lastResult: null,
    isHost: false,
    hostIsTemporary: false,
    closing: false,
    view: {},
    legalMoves: [],
    gameOver: null,
    connected: false,
    lastError: null,
  };
}

/**
 * The match refused us, as opposed to the network failing.
 *
 * A distinct type rather than a flag on the message, because the two failures
 * arrive as the same kind of thrown value and only their cause differs.
 */
class MatchGone extends Error {}

/**
 * How long to keep trying before admitting the room is not coming back.
 *
 * Generous: a phone that woke on a bad connection deserves more than a couple
 * of attempts, and being thrown out of a live game is worse than waiting.
 */
const GIVE_UP_MS = 45_000;

const decoder = new TextDecoder();

function decodeData(data: unknown): Record<string, unknown> {
  if (typeof data === 'string') return JSON.parse(data) as Record<string, unknown>;
  return JSON.parse(decoder.decode(data as Uint8Array)) as Record<string, unknown>;
}

export interface MatchHandle {
  send(move: string, args: string[]): void;
  /** Host-only. Carries the TABLE version: these act on the public roster. */
  host(action: Record<string, unknown>): void;
  /** Say one of the table's canned phrases, by id. */
  say(phraseId: string): void;
  /** Typed words, which the host can switch off. Tidied and limited on arrival. */
  type(text: string): void;
  /**
   * This client's whole voice state, sent as a whole.
   *
   * Idempotent, so it is safe to send on every change and safe to send twice.
   */
  setVoice(state: { joined: boolean; micOff: boolean; earsOff: boolean }): void;
  /**
   * Wear a face at this table (D92).
   *
   * Match-local by construction: there is no profile write anywhere on this
   * path, so the borrow ends when the match does.
   */
  wear(avatarId: string): void;
  /**
   * Tell the match that what you are wearing has changed.
   *
   * Sent after an equip, which happens over an RPC outside the match - so
   * without this the change lands on the server and reaches nobody until you
   * rejoin. Carries nothing: the server re-reads the row it wrote itself.
   */
  lookAgain(): void;
  /** A signalling blob for one peer. Forwarded by the server without reading. */
  signal(to: string, kind: 'offer' | 'answer' | 'candidate' | 'bye', payload: string): void;
  /** Called with a blob a peer sent us. Replaces any previous listener. */
  onSignal(handler: ((from: string, kind: string, payload: string) => void) | null): void;
  sync(): void;
  leave(): Promise<void>;
}

/**
 * Connect to a match and keep it connected.
 *
 * `onChange` is called with a fresh `MatchView` whenever anything moves.
 */
export async function connectMatch(
  session: Session,
  matchId: string,
  onChange: (view: MatchView) => void,
  /**
   * Ask to watch rather than play.
   *
   * The shared table screen sets this. It is a television, not a player: left
   * to the default it would take seat 0, costing the room a seat and putting a
   * phantom in the game.
   */
  spectate = false,
): Promise<MatchHandle> {
  let state = emptyView();
  /** The last public view, kept separately so an overlay can be re-applied. */
  let tableView: GameView = {};
  let personalView: GameView | null = null;

  let socket: Socket | null = null;
  /*
   * Where a signalling blob goes when one arrives.
   *
   * A single listener rather than a list: exactly one thing manages the peer
   * connections, and two of them would fight over the same offers.
   */
  let onSignalHandler: ((from: string, kind: string, payload: string) => void) | null = null;
  let closed = false;
  let reconnecting = false;

  const publish = () => {
    state = { ...state, view: mergeView(tableView, personalView) };
    onChange(state);
  };

  const handle = (opCode: number, body: Record<string, unknown>) => {
    if (opCode === ServerOpCode.ERROR) {
      state = {
        ...state,
        lastError: { code: String(body['code']), message: String(body['message'] ?? '') },
      };
      publish();
      return;
    }

    if (opCode === ServerOpCode.GAME_OVER) {
      state = { ...state, gameOver: body['result'] ?? true };
      publish();
      return;
    }

    if (opCode === ServerOpCode.VOICE) {
      /*
       * Straight through to whoever is managing the peer connections.
       *
       * Not stored and not put in the view: a blob is a step in a handshake
       * between two browsers, and it means nothing a moment later.
       */
      const blob = body as { from?: string; kind?: string; payload?: string };
      if (onSignalHandler && typeof blob.from === 'string') {
        onSignalHandler(blob.from, String(blob.kind ?? ''), String(blob.payload ?? ''));
      }
      return;
    }

    if (opCode !== ServerOpCode.VIEW) return;

    const table = body['table'] as
      | {
          v: number;
          view: GameView;
          seats: SeatInfo[];
          observers?: ObserverInfo[];
          activeSeats: number[];
          phase: string | null;
          turn: TurnInfo | null;
          paused: boolean;
          lastMove?: { n: number; seat: number; move: string } | null;
          waiting?: number;
          says?: Record<string, { id?: string; text?: string; n: number }>;
          gameId?: string;
          lifecycle?: Lifecycle;
          endedByHost?: boolean;
          skin?: { deckId: string; tableId: string; avatarIds?: string[]; by?: string } | null;
          round?: number;
          autoContinue?: boolean;
          seatCount?: number;
          allowObservers?: boolean;
          allowTextChat?: boolean;
          allowVoice?: boolean;
          voice?: Record<string, VoiceMember>;
          seatRange?: { min: number; max: number };
          seated?: number;
          turnLimitMs?: number;
          variants?: Record<string, boolean | string | number>;
          hostIsTemporary?: boolean;
          closing?: boolean;
          lastResult?: unknown;
        }
      | undefined;

    if (table) {
      tableView = table.view ?? {};
      const seats = table.seats ?? [];
      state = {
        ...state,
        tableV: table.v,
        seats,
        observers: table.observers ?? [],
        activeSeats: table.activeSeats ?? [],
        phase: table.phase ?? null,
        turn: table.turn ?? null,
        paused: table.paused === true,
        lastMove: table.lastMove ?? null,
        waiting: table.waiting ?? 0,
        says: table.says ?? {},
        gameId: table.gameId ?? state.gameId,
        lifecycle: table.lifecycle ?? 'playing',
        endedByHost: table.endedByHost ?? false,
        skin: table.skin ?? null,
        round: table.round ?? 0,
        /*
         * A result belongs to the round that produced it.
         *
         * This was set on GAME_OVER and never cleared, so from the first
         * finished round onward every client showed "Finished" for the rest of
         * the room's life - through round two, three and beyond.
         */
        gameOver: table.lifecycle === 'playing' ? null : state.gameOver,
        autoContinue: table.autoContinue !== false,
        seatCount: table.seatCount ?? seats.length,
        allowObservers: table.allowObservers !== false,
        allowTextChat: table.allowTextChat !== false,
        // `!== false` like its sibling: a server that has not been redeployed
        // sends neither field, and the safe reading of silence is "allowed".
        allowVoice: table.allowVoice !== false,
        voice: table.voice ?? {},
        seatRange: table.seatRange ?? state.seatRange,
        seated: table.seated ?? seats.length,
        turnLimitMs: table.turnLimitMs ?? state.turnLimitMs,
        variants: table.variants ?? state.variants,
        hostIsTemporary: table.hostIsTemporary === true,
        closing: table.closing === true,
        lastResult: table.lastResult ?? null,
        // Recomputed from the roster each time, so a host handover lands
        // without a separate message.
        isHost:
          state.seat !== null &&
          seats.some((info) => info.seat === state.seat && info.isHost),
      };
    }

    /*
     * The chat log, in its own section with its own version.
     *
     * Absent means unchanged, exactly as for the other two — which is the point
     * of it being separate: a table where nobody is talking never re-sends the
     * history, and one where somebody is never re-sends the cards.
     */
    const chat = body['chat'] as { v: number; log?: ChatEntry[] } | undefined;
    if (chat) {
      state = { ...state, chatV: chat.v, chatLog: chat.log ?? [] };
    }

    const personal = body['personal'] as
      | { v: number; seat: number; view: GameView; legalMoves: LegalMove[] }
      | undefined;

    if (personal) {
      personalView = personal.view ?? {};
      state = {
        ...state,
        personalV: personal.v,
        seat: personal.seat,
        legalMoves: personal.legalMoves ?? [],
        // A fresh decision surface supersedes whatever went wrong last time.
        lastError: null,
      };
    }

    publish();
  };

  const open = async (): Promise<void> => {
    const next = client.createSocket(useSSL, false);

    next.onmatchdata = (matchData) => {
      handle(Number(matchData.op_code), decodeData(matchData.data));
    };

    next.ondisconnect = () => {
      if (closed) return;
      state = { ...state, connected: false };
      publish();
      void reconnect();
    };

    await next.connect(session, false);

    /*
     * Connecting and joining fail for different reasons, and only one of them
     * is worth retrying.
     *
     * A socket that will not connect is the network, or the server being
     * restarted - keep trying. A socket that connects and is then refused the
     * MATCH is a room that no longer exists: an empty one is terminated after
     * ten minutes, and no amount of retrying brings it back. Telling those two
     * apart is what stops a dead room showing "Reconnecting..." forever.
     */
    try {
      await next.joinMatch(matchId, undefined, spectate ? { spectate: 'true' } : undefined);
    } catch (error) {
      next.disconnect(false);
      throw new MatchGone(String(error));
    }

    socket = next;

    state = { ...state, connected: true };
    publish();

    // Whatever we missed while away. The server answers with both sections in
    // full, so a client that has been asleep for ten minutes is correct again
    // in one round trip rather than guessing from deltas.
    requestSync();
  };

  /**
   * Reconnect with backoff.
   *
   * A phone that locks drops the socket, and every player in the room does it
   * at once when a hand ends and everyone looks away. Backoff keeps a flaky
   * network from turning into a reconnect storm.
   */
  const reconnect = async (): Promise<void> => {
    if (closed || reconnecting) return;
    reconnecting = true;

    /*
     * Refusals are counted rather than acted on at once.
     *
     * A server restarting can refuse a join for a moment while it comes back,
     * and treating the first refusal as "the room is gone" would evict a whole
     * table over a blip. Three in a row is not a blip.
     */
    let refusals = 0;
    const startedAt = Date.now();

    for (let attempt = 0; !closed; attempt++) {
      const wait = Math.min(500 * 2 ** attempt, 10_000);
      await new Promise((resolve) => setTimeout(resolve, wait));
      if (closed) break;
      try {
        await open();
        break;
      } catch (error) {
        if (error instanceof MatchGone) refusals += 1;

        /*
         * Give up, and say so.
         *
         * Either the room refused us repeatedly, or we have been trying long
         * enough that something is properly wrong. Both end the same way for
         * the player: this screen is never coming back, so stop pretending it
         * might and let them go and join another game.
         */
        if (refusals >= 3 || Date.now() - startedAt > GIVE_UP_MS) {
          state = { ...state, connected: false, gone: true };
          publish();
          break;
        }
      }
    }
    reconnecting = false;
  };

  const requestSync = () => {
    if (!socket) return;
    void socket.sendMatchState(matchId, ClientOpCode.SYNC_REQUEST, JSON.stringify({}));
  };

  await open();

  /*
   * A backgrounded tab is often not *told* its socket died - iOS in particular
   * freezes the tab and the close event never arrives. Re-checking on
   * visibility change is what stops a player returning to a frozen board.
   */
  const onVisible = () => {
    if (document.visibilityState !== 'visible' || closed) return;
    // Nothing to come back to: the room was given up on while we were away.
    if (state.gone) return;
    if (socket && state.connected) requestSync();
    else void reconnect();
  };
  document.addEventListener('visibilitychange', onVisible);

  return {
    send(move, args) {
      if (!socket) return;
      void socket.sendMatchState(
        matchId,
        ClientOpCode.MOVE,
        // The PERSONAL version. A move is a decision about this seat's own
        // surface, and the table moving underneath it is not a conflict.
        JSON.stringify({ move, args, v: state.personalV }),
      );
    },
    host(action) {
      if (!socket) return;
      void socket.sendMatchState(
        matchId,
        ClientOpCode.HOST_ACTION,
        // The TABLE version, not the personal one: "remove seat 2" must not
        // land after seat 2 became somebody else.
        JSON.stringify({ ...action, v: state.tableV }),
      );
    },
    say(phraseId) {
      if (!socket) return;
      // No version: a remark is not an action on the game state, so there is
      // nothing for it to be stale against.
      void socket.sendMatchState(matchId, ClientOpCode.CHAT, JSON.stringify({ phraseId }));
    },
    lookAgain() {
      if (!socket) return;
      void socket.sendMatchState(matchId, ClientOpCode.LOOK, JSON.stringify({}));
    },

    wear(avatarId) {
      if (!socket) return;
      // No version, like a remark: a face is not an action on the game state.
      void socket.sendMatchState(matchId, ClientOpCode.WEAR, JSON.stringify({ avatarId }));
    },

    setVoice(voice) {
      if (!socket) return;
      // No version: being in the conversation is not an action on the game.
      void socket.sendMatchState(matchId, ClientOpCode.VOICE_STATE, JSON.stringify(voice));
    },
    signal(to, kind, payload) {
      if (!socket) return;
      void socket.sendMatchState(matchId, ClientOpCode.VOICE, JSON.stringify({ to, kind, payload }));
    },
    onSignal(handler) {
      onSignalHandler = handler;
    },
    type(text) {
      if (!socket) return;
      /*
       * The words, not an id — the other half of `CHAT`.
       *
       * Sent as typed rather than as tidied: the protocol normalises on arrival
       * and that is the copy everybody sees, so a client that tidied first
       * would only be guessing at the same answer.
       */
      void socket.sendMatchState(matchId, ClientOpCode.CHAT, JSON.stringify({ text }));
    },
    sync: requestSync,
    async leave() {
      closed = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (socket) {
        try {
          await socket.leaveMatch(matchId);
        } catch {
          /* already gone */
        }
        socket.disconnect(false);
        socket = null;
      }
    },
  };
}
